import React, { useState, useEffect } from 'react'
import {
  Grid2 as Grid,
  MenuItem,
  Select,
  FormControl,
  FormControlLabel,
  InputLabel,
  Box,
  Button,
  Switch,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material'
import { TextField } from 'components/@extended/TextField'
import FormulaTextField, { VariableSuggestion } from 'components/FormulaTextField'
import { CopyOutlined, DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import IconButton from 'components/@extended/IconButton'
import Tooltip from 'components/@extended/Tooltip'
import { Period } from 'types/periods'
import { RowType, VariableAllocationRow } from 'types/comp-allocations'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import { ExpandedTarget } from 'types/components'

interface FormulaRow {
  requirement: string
  formula: string
}

interface AdvancedEditorContentProps {
  row: VariableAllocationRow
  period: Period | null
  expandedTargets: ExpandedTarget[]
  onSave: (updatedRow: VariableAllocationRow) => void
  onCancel: () => void
}

const VAR_OPTIONS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

interface VariableMapping {
  targetVar: string
  metricVar: string
  targetSlug: string
  metricSlug: string
  componentLabel: string
}

const AdvancedEditorContent: React.FC<AdvancedEditorContentProps> = ({
  row,
  period,
  expandedTargets,
  onSave,
  onCancel,
}) => {
  const theme = useTheme()
  const [formData, setFormData] = useState<Partial<VariableAllocationRow>>({})
  const [formulas, setFormulas] = useState<FormulaRow[]>([])
  const [editIndex, setEditIndex] = useState<number | null>(null)

  // Build variable mappings from expanded targets (slug -> human-readable)
  const variableMap = React.useMemo(() => {
    const map: VariableMapping[] = []
    expandedTargets.forEach((et, index) => {
      if (index < VAR_OPTIONS.length) {
        map.push({
          targetVar: `Target ${VAR_OPTIONS[index]}`,
          metricVar: `Actual ${VAR_OPTIONS[index]}`,
          targetSlug: et.target?.slug || '',
          metricSlug: et.component?.slug || '',
          componentLabel: et.component?.label || '',
        })
      }
    })
    return map
  }, [expandedTargets])

  const suggestions: VariableSuggestion[] = React.useMemo(
    () =>
      variableMap.flatMap((vm) => [
        { label: vm.targetVar, value: vm.targetVar, category: 'target' as const, componentLabel: vm.componentLabel },
        { label: vm.metricVar, value: vm.metricVar, category: 'actual' as const, componentLabel: vm.componentLabel },
      ]),
    [variableMap]
  )

  const replaceSlugsWithVariables = React.useCallback(
    (text: string): string => {
      let result = text
      variableMap.forEach((vm) => {
        if (vm.targetSlug) {
          result = result.replaceAll(vm.targetSlug, vm.targetVar)
        }
        if (vm.metricSlug) {
          result = result.replaceAll(vm.metricSlug, vm.metricVar)
        }
      })
      return result
    },
    [variableMap]
  )

  const replaceVariablesWithSlugs = React.useCallback(
    (text: string): string => {
      let result = text
      variableMap.forEach((vm) => {
        if (vm.targetVar) {
          result = result.replaceAll(vm.targetVar, vm.targetSlug)
        }
        if (vm.metricVar) {
          result = result.replaceAll(vm.metricVar, vm.metricSlug)
        }
      })
      return result
    },
    [variableMap]
  )

  // Helper to convert decimal to percentage for display
  const toPercent = (val: number | null | undefined) => (val ? +(val * 100).toFixed(4) : 0)
  // Helper to convert percentage input back to decimal for storage
  const fromPercent = (val: number) => +(val / 100).toFixed(6)

  useEffect(() => {
    if (row) {
      setFormData({
        label: row.label || '',
        rowType: row.rowType || RowType.CALCULATION_ROW,
        periodId: row.periodId || '',
        allocationPercentage: toPercent(row.allocationPercentage),
        minAllocationPercentage: toPercent(row.minAllocationPercentage),
        maxAllocationPercentage: toPercent(row.maxAllocationPercentage),
        defaultPayout: toPercent(row.defaultPayout),
        minPayout: toPercent(row.minPayout),
        maxPayout: toPercent(row.maxPayout),
        achievementMultiplier: row.achievementMultiplier || '',
        restrictedVisibility: row.restrictedVisibility || false,
      })

      // Build paired formula rows from parallel arrays
      const reqs = row.formulaRequirements || []
      const opts = row.formulaOptions || []
      const maxLen = Math.max(reqs.length, opts.length)
      const paired: FormulaRow[] = []
      for (let i = 0; i < maxLen; i++) {
        paired.push({
          requirement: replaceSlugsWithVariables(reqs[i] || ''),
          formula: replaceSlugsWithVariables(opts[i] || ''),
        })
      }
      setFormulas(paired)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row])

  // Re-map formulas when variable map becomes available after initial load
  useEffect(() => {
    if (variableMap.length > 0 && formulas.length > 0) {
      setFormulas((prev) =>
        prev.map((f) => ({
          requirement: replaceSlugsWithVariables(replaceVariablesWithSlugs(f.requirement)),
          formula: replaceSlugsWithVariables(replaceVariablesWithSlugs(f.formula)),
        }))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableMap])

  const handleInputChange = (field: keyof VariableAllocationRow, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddFormula = () => {
    setFormulas([...formulas, { requirement: '', formula: '' }])
    setEditIndex(formulas.length)
  }

  const handleUpdateFormula = (index: number, field: keyof FormulaRow, value: string) => {
    const updated = [...formulas]
    updated[index] = { ...updated[index], [field]: value }
    setFormulas(updated)
  }

  const handleDeleteFormula = (index: number) => {
    setFormulas(formulas.filter((_, i) => i !== index))
    if (editIndex === index) setEditIndex(null)
  }

  const handleSave = () => {
    // Convert human-readable variables back to slugs for storage
    const validFormulas = formulas.filter((f) => f.requirement.trim() !== '' || f.formula.trim() !== '')
    const updatedRow = {
      ...row,
      ...formData,
      // Convert percentage display values back to decimals for storage
      allocationPercentage: fromPercent(formData.allocationPercentage as number),
      minAllocationPercentage: fromPercent(formData.minAllocationPercentage as number),
      maxAllocationPercentage: fromPercent(formData.maxAllocationPercentage as number),
      defaultPayout: fromPercent(formData.defaultPayout as number),
      minPayout: fromPercent(formData.minPayout as number),
      maxPayout: fromPercent(formData.maxPayout as number),
      formulaRequirements: validFormulas.map((f) => replaceVariablesWithSlugs(f.requirement)),
      formulaOptions: validFormulas.map((f) => replaceVariablesWithSlugs(f.formula)),
    } as VariableAllocationRow
    onSave(updatedRow)
  }

  return (
    <>
      <Box id="edit-allocation-row-dialog" sx={{ mt: 2 }}>
        <Grid container spacing={3}>
          {/* Label */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Label"
              value={formData.label || ''}
              onChange={(e) => handleInputChange('label', e.target.value)}
              variant="outlined"
              pill
              shrink
            />
          </Grid>

          {/* Row Type */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.rowType || RowType.CALCULATION_ROW}
                onChange={(e) => handleInputChange('rowType', e.target.value)}
                label="Type"
                style={{ borderRadius: '21px' }}
              >
                <MenuItem value={RowType.DISPLAY_ROW}>Display Only</MenuItem>
                <MenuItem value={RowType.CALCULATION_ROW}>Variable</MenuItem>
                <MenuItem value={RowType.FIXED_VALUE_ROW}>Fixed Value</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Period */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SinglePeriodSelector
              value={formData.periodId || ''}
              label="Period"
              onChange={(value) => handleInputChange('periodId', value)}
              periods={period ? [period] : []}
              rootPeriodOnly={false}
              sortOrder="asc"
              variant="outlined"
              shrink
              pill
            />
          </Grid>

          {/* Allocation Percentage */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Allocation %"
              type="number"
              value={formData.allocationPercentage || ''}
              onChange={(e) => handleInputChange('allocationPercentage', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
          </Grid>

          {/* Min Allocation Percentage */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Min Allocation %"
              type="number"
              value={formData.minAllocationPercentage || ''}
              onChange={(e) => handleInputChange('minAllocationPercentage', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
          </Grid>

          {/* Max Allocation Percentage */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Max Allocation %"
              type="number"
              value={formData.maxAllocationPercentage || ''}
              onChange={(e) => handleInputChange('maxAllocationPercentage', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 100,
                step: 1,
              }}
            />
          </Grid>

          {/* Default Payout */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Default Payout %"
              type="number"
              value={formData.defaultPayout || ''}
              onChange={(e) => handleInputChange('defaultPayout', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 500,
                step: 1,
              }}
            />
          </Grid>

          {/* Min Payout */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Min Payout %"
              type="number"
              value={formData.minPayout || ''}
              onChange={(e) => handleInputChange('minPayout', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 500,
                step: 1,
              }}
            />
          </Grid>

          {/* Max Payout */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Max Payout %"
              type="number"
              value={formData.maxPayout || ''}
              onChange={(e) => handleInputChange('maxPayout', parseFloat(e.target.value) || 0)}
              variant="outlined"
              pill
              shrink
              inputProps={{
                min: 0,
                max: 500,
                step: 1,
              }}
            />
          </Grid>

          {/* Achievement Multiplier */}
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Accelerator"
              value={formData.achievementMultiplier || ''}
              onChange={(e) => handleInputChange('achievementMultiplier', e.target.value)}
              variant="outlined"
              pill
              shrink
              placeholder="e.g., a**1.00"
            />
          </Grid>

          {/* Restricted Visibility */}
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.restrictedVisibility}
                  onChange={(e) => handleInputChange('restrictedVisibility', e.target.checked)}
                />
              }
              label="Restricted Visibility"
            />
          </Grid>

          {/* Paired Requirement / Formula Table */}
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={0} sx={{ mb: 1 }}>
              <Grid size={{ xs: 5 }}>
                <Typography variant="subtitle1" align="center">
                  Requirement
                </Typography>
              </Grid>
              <Grid size={{ xs: 5 }}>
                <Typography variant="subtitle1" align="center">
                  Formula
                </Typography>
              </Grid>
              <Grid size={{ xs: 2 }} />
            </Grid>

            {formulas.map((formulaRow, index) => (
              <React.Fragment key={index}>
                <Grid container spacing={1} sx={{ py: 1, alignItems: 'center' }}>
                  <Grid size={{ xs: 5 }}>
                    {editIndex === index ? (
                      <FormulaTextField
                        value={formulaRow.requirement}
                        onChange={(val) => handleUpdateFormula(index, 'requirement', val)}
                        onCommit={() => setEditIndex(null)}
                        suggestions={suggestions}
                        placeholder="e.g., Actual A >= 0.9700"
                        autoFocus
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          minHeight: 40,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                          borderRadius: 1,
                        }}
                        onClick={() => setEditIndex(index)}
                      >
                        <Typography variant="body2" sx={{ fontStyle: formulaRow.requirement ? 'italic' : 'normal' }}>
                          {formulaRow.requirement || 'Click to edit...'}
                        </Typography>
                        <Tooltip title="Edit Requirement">
                          <EditOutlined style={{ fontSize: 14, opacity: 0.5 }} />
                        </Tooltip>
                      </Box>
                    )}
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    {editIndex === index ? (
                      <FormulaTextField
                        value={formulaRow.formula}
                        onChange={(val) => handleUpdateFormula(index, 'formula', val)}
                        onCommit={() => setEditIndex(null)}
                        suggestions={suggestions}
                        placeholder="e.g., 1.20"
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: 1,
                          minHeight: 40,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                          borderRadius: 1,
                        }}
                        onClick={() => setEditIndex(index)}
                      >
                        <Typography variant="body2" sx={{ fontStyle: formulaRow.formula ? 'italic' : 'normal' }}>
                          {formulaRow.formula || 'Click to edit...'}
                        </Typography>
                        <Tooltip title="Edit Formula">
                          <EditOutlined style={{ fontSize: 14, opacity: 0.5 }} />
                        </Tooltip>
                      </Box>
                    )}
                  </Grid>
                  <Grid size={{ xs: 2 }} sx={{ textAlign: 'center' }}>
                    <Tooltip title="Delete Formula">
                      <IconButton size="small" color="error" onClick={() => handleDeleteFormula(index)}>
                        <DeleteOutlined />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
                {index < formulas.length - 1 && <Divider />}
              </React.Fragment>
            ))}

            <Box sx={{ mt: 1 }}>
              <Button size="small" color="primary" startIcon={<PlusOutlined />} onClick={handleAddFormula}>
                Add Another Formula
              </Button>
            </Box>
          </Grid>

          {/* Component Variables Legend */}
          {variableMap.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Accordion defaultExpanded={false} variant="outlined">
                <AccordionSummary expandIcon={<DownOutlined />}>
                  <Typography variant="subtitle1">Component Variables Legend</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table stickyHeader size="small" aria-label="component variables table">
                      <TableHead
                        sx={{
                          '& th': {
                            borderTop: `1px solid ${theme.palette.divider}`,
                            borderBottom: `2px solid ${theme.palette.divider} !important`,
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell>Component</TableCell>
                          <TableCell>Target Variable</TableCell>
                          <TableCell>Actual Variable</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variableMap.map((vm, index) => (
                          <TableRow key={index}>
                            <TableCell>{vm.componentLabel}</TableCell>
                            <TableCell>
                              <Chip
                                label={vm.targetVar}
                                size="small"
                                variant="outlined"
                                deleteIcon={<CopyOutlined />}
                                onDelete={() => navigator.clipboard.writeText(vm.targetVar)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={vm.metricVar}
                                size="small"
                                variant="outlined"
                                deleteIcon={<CopyOutlined />}
                                onDelete={() => navigator.clipboard.writeText(vm.metricVar)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}
        </Grid>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Changes
        </Button>
      </Box>
    </>
  )
}

export default AdvancedEditorContent
